/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef } from 'react';

/**
 * Drives a horizontal (column) drag-to-resize interaction. Returns a
 * pointer-down handler to attach to a divider or panel edge; while the pointer
 * is held it forwards every `pointermove` to `onMove`, which is responsible for
 * computing and committing the new width. Listeners are registered on `window`
 * so the drag keeps tracking outside the handle, and are torn down on
 * pointerup/cancel/blur and on unmount.
 */
export function useHorizontalResize(
  onMove: (event: globalThis.PointerEvent) => void,
): (event: ReactPointerEvent<HTMLElement>) => void {
  const cleanupRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => () => cleanupRef.current?.(), []);

  return useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      event.preventDefault();
      cleanupRef.current?.();
      const abortController = new AbortController();
      const cleanup = () => {
        abortController.abort();
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        if (cleanupRef.current === cleanup) {
          cleanupRef.current = undefined;
        }
      };
      cleanupRef.current = cleanup;
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      const options = { signal: abortController.signal };
      window.addEventListener('pointermove', onMove, options);
      window.addEventListener('pointerup', cleanup, options);
      window.addEventListener('pointercancel', cleanup, options);
      window.addEventListener('blur', cleanup, options);
    },
    [onMove],
  );
}
